import React, { useMemo } from "react";
import { FiCalendar, FiFileText } from "react-icons/fi";

import Reveal from "../../../components/ui/Reveal";

import { sanitizePublicHtml } from "../utils/publicPageUtils";

function formatDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function PagePublicContent({ page }) {
  const safeHtml = useMemo(() => {
    return sanitizePublicHtml(page?.content_html);
  }, [page?.content_html]);

  const updatedLabel = formatDate(page?.updated_at || page?.created_at);

  return (
    <section className="mx-auto max-w-[1180px] px-4 pb-16 pt-6 sm:px-6 md:pt-8">
      <Reveal>
        <div className="mb-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#1277B9]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
                <FiFileText className="h-4 w-4" />
                Page d’information
              </div>

              {page?.subtitle && (
                <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300 md:text-base">
                  {page.subtitle}
                </p>
              )}
            </div>

            {updatedLabel && (
              <div className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                <FiCalendar className="h-4 w-4" />
                Mis à jour le {updatedLabel}
              </div>
            )}
          </div>
        </div>
      </Reveal>

      <Reveal>
        <article
          className="
            rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-7 md:p-8

            prose prose-slate max-w-none dark:prose-invert
            prose-headings:font-bold prose-headings:tracking-tight
            prose-h1:text-3xl prose-h1:md:text-4xl
            prose-h2:mt-10 prose-h2:border-b prose-h2:border-slate-200 prose-h2:pb-3 prose-h2:text-2xl prose-h2:dark:border-slate-800
            prose-h3:text-xl
            prose-p:leading-8
            prose-a:font-bold prose-a:text-[#1277B9] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-slate-950 dark:prose-strong:text-white
            prose-ul:leading-8 prose-ol:leading-8
            prose-blockquote:rounded-3xl prose-blockquote:border-l-4 prose-blockquote:border-[#1277B9] prose-blockquote:bg-sky-50 prose-blockquote:px-5 prose-blockquote:py-3 prose-blockquote:not-italic dark:prose-blockquote:bg-sky-500/10
            prose-img:mx-auto prose-img:max-h-[620px] prose-img:rounded-[1.5rem] prose-img:border prose-img:border-slate-200 prose-img:bg-slate-50 prose-img:object-contain prose-img:shadow-sm dark:prose-img:border-slate-800 dark:prose-img:bg-slate-900

            [&_table]:min-w-[680px] [&_table]:border-collapse
            [&_th]:border [&_th]:border-slate-200 [&_th]:bg-slate-50 [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:text-sm [&_th]:font-bold
            [&_td]:border [&_td]:border-slate-200 [&_td]:px-4 [&_td]:py-3 [&_td]:text-sm
            dark:[&_th]:border-slate-800 dark:[&_th]:bg-slate-900 dark:[&_td]:border-slate-800

            [&_iframe]:aspect-video [&_iframe]:w-full [&_iframe]:rounded-[1.5rem] [&_iframe]:border [&_iframe]:border-slate-200 dark:[&_iframe]:border-slate-800
            [&_video]:w-full [&_video]:rounded-[1.5rem]
          "
        >
          <div
            className="overflow-x-auto"
            dangerouslySetInnerHTML={{
              __html: safeHtml,
            }}
          />
        </article>
      </Reveal>
    </section>
  );
}